import React, { useMemo, useState } from 'react';

import shuffle from 'lodash/shuffle';

import { SimpleSurvey } from '../../SimpleSurvey';

import { categories } from './categories';
import { CreateFirstCard } from './CreateFirstCard';
import { CreateFirstSearchBar } from './CreateFirstSearchBar';
import {
  CardsContainer,
  CreateFirstBody,
  HideDesktop,
  HideMobile,
  Wrapper,
} from './styles';
import type {
  CreateFirstAnswerType,
  CreateFirstUseCase,
  TransformedUseCase,
} from './types';
import { filterUseCasesByQuery, transformToCreateFirstUseCase } from './utils';

export const aiImages: CreateFirstUseCase = {
  slug: 'ai-images',
  name: 'AI Images',
  type: 'aiImages',
  imagePath: '/images/category/ai-images.png',
};

export interface CreateFirstProps {
  useCases?: TransformedUseCase[];
  onAnswer: (answer: { value: string; type: CreateFirstAnswerType }) => void;
  isDisabled?: boolean;
}

export const CreateFirst: React.FC<CreateFirstProps> = ({
  useCases = [],
  onAnswer,
  isDisabled = false,
}) => {
  const [query, setQuery] = useState('');

  // Transform use cases to CreateFirstUseCase type upfront
  const createFirstUseCases = useMemo(() => {
    const transformed = useCases.map(transformToCreateFirstUseCase);
    // Add aiImages to the end of the array
    const transformedWithAiImages = [...transformed, aiImages];

    return shuffle(transformedWithAiImages);
  }, [useCases]);

  // search and filter use cases based on query
  const filteredUseCases = useMemo(() => {
    return filterUseCasesByQuery(createFirstUseCases, query);
  }, [query, createFirstUseCases]);

  const handleChange = (query: string): void => {
    setQuery(query);
  };

  const handleCardClick = (item: CreateFirstUseCase): void => {
    onAnswer({ value: item.slug, type: item.type });
  };

  const itemsToShow = !query ? shuffle(categories) : filteredUseCases;

  return (
    <Wrapper>
      <SimpleSurvey.Header step={4} title="What do you want to create?" />
      <CreateFirstBody>
        <CreateFirstSearchBar query={query} onChange={handleChange} />
        <HideDesktop>
          <SimpleSurvey.List>
            {itemsToShow.map((item) => (
              <SimpleSurvey.Option
                key={item.slug}
                isDisabled={isDisabled}
                label={item.name}
                onClick={() => handleCardClick(item)}
              />
            ))}
          </SimpleSurvey.List>
        </HideDesktop>
        <HideMobile>
          <CardsContainer>
            {itemsToShow.map((item) => (
              <CreateFirstCard
                key={item.slug}
                isDisabled={isDisabled}
                item={item}
                onSelect={handleCardClick}
              />
            ))}
          </CardsContainer>
        </HideMobile>
      </CreateFirstBody>
    </Wrapper>
  );
};
